import type { FC } from 'react';

import {
  Heading,
  InputEmail,
  InputText,
  InputTelephone,
  Grid,
  InputNumber,
  RadioGroup,
  RadioButton,
} from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import type { ContactFormRendering } from '@sitecore/types/ContactForm';
import { UseFormRegister, FieldErrors, Controller, Control } from 'react-hook-form';

import type { FormFields } from '../util/types';

type Props = {
  register: UseFormRegister<FormFields>;
  fields: ContactFormRendering['fields'];
  control: Control<FormFields, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<FormFields>;
};

const BudgetManagerContactInputs: FC<Props> = ({ register, control, fields, errors }) => {
  const {
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    phoneNumberFormField,
    emailAddressFormField,
    businessNameFormField,
    postalCodeFormField,
    houseNumberFormField,
    houseNumberSuffixFormField,
    streetNameFormField,
    cityFormField,
    salutationFormField,
  } = fields.formFields;
  return (
    <>
      <Heading size="S" as="h3">
        {wrap(fields.content.budgetManagerContactDetailsTitle)}
      </Heading>
      <InputText
        {...register('budgetManagerBusiness')}
        error={errors.budgetManagerBusiness?.message}
        label={businessNameFormField.value.label}
        placeholder={businessNameFormField.value.placeholder}
        hint={businessNameFormField.value.hint}
      />
      <Controller
        control={control}
        name="budgetManagerSalutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={salutationFormField.value.label}
            hint={salutationFormField.value.hint}
            value={value}
            onValueChange={onChange}
            error={errors.budgetManagerSalutation?.message}>
            {salutationFormField?.value?.options?.map(option => (
              <RadioButton value={option.name} key={option.name}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6" alignY="end">
        <InputText
          {...register('budgetManagerFirstname')}
          label={firstNameFormField.value.label}
          error={errors.budgetManagerFirstname?.message}
          placeholder={firstNameFormField.value.placeholder}
          hint={firstNameFormField.value.hint}
        />
        <InputText
          {...register('budgetManagerSurnamePreposition')}
          label={surnamePrepositionFormField.value.label}
          error={errors.budgetManagerSurnamePreposition?.message}
          isOptional
          placeholder={surnamePrepositionFormField.value.placeholder}
          hint={surnamePrepositionFormField.value.hint}
        />
        <InputText
          {...register('budgetManagerSurname')}
          label={surnameFormField.value.label}
          error={errors.budgetManagerSurname?.message}
          placeholder={surnameFormField.value.placeholder}
          hint={surnameFormField.value.hint}
        />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2' }} gap="6">
        <InputTelephone
          {...register('budgetManagerPhoneNumber')}
          label={phoneNumberFormField.value.label}
          error={errors.budgetManagerPhoneNumber?.message}
          autoComplete="tel"
          placeholder={phoneNumberFormField.value.placeholder}
          hint={phoneNumberFormField.value.hint}
        />
        <InputEmail
          {...register('budgetManagerEmailAddress')}
          label={emailAddressFormField.value.label}
          error={errors.budgetManagerEmailAddress?.message}
          autoComplete="email"
          placeholder={emailAddressFormField.value.placeholder}
          hint={emailAddressFormField.value.hint}
        />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="6">
        <InputText
          {...register('budgetManagerPostalCode')}
          label={postalCodeFormField.value.label}
          error={errors.budgetManagerPostalCode?.message}
          placeholder={postalCodeFormField.value.placeholder}
          hint={postalCodeFormField.value.hint}
        />
        <InputNumber
          {...register('budgetManagerHouseNumber')}
          label={houseNumberFormField.value.label}
          error={errors.budgetManagerHouseNumber?.message}
          formatOptions={{ useGrouping: false }}
          placeholder={houseNumberFormField.value.placeholder}
          hint={houseNumberFormField.value.hint}
        />
        <InputText
          {...register('budgetManagerHouseNumberSuffix')}
          label={houseNumberSuffixFormField.value.label}
          error={errors.budgetManagerHouseNumberSuffix?.message}
          isOptional
          placeholder={houseNumberSuffixFormField.value.placeholder}
          hint={houseNumberSuffixFormField.value.hint}
        />
      </Grid>
      <InputText
        {...register('budgetManagerStreetName')}
        label={streetNameFormField.value.label}
        error={errors.budgetManagerStreetName?.message}
        placeholder={streetNameFormField.value.placeholder}
        hint={streetNameFormField.value.hint}
      />
      <InputText
        {...register('budgetManagerCity')}
        label={cityFormField.value.label}
        error={errors.budgetManagerCity?.message}
        placeholder={cityFormField.value.placeholder}
        hint={cityFormField.value.hint}
      />
    </>
  );
};

export default BudgetManagerContactInputs;
